import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format, addDays, startOfDay, endOfDay, isSameDay } from 'date-fns';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Calendar as CalendarIcon, 
  Clock, 
  User, 
  Mail, 
  ChevronLeft, 
  ChevronRight,
  CheckCircle2,
  Loader2,
  MapPin,
  Video,
  ArrowLeft,
  Sparkles,
  Zap,
  Shield,
  Star
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Skeleton } from '../components/ui/skeleton';

// Very lightweight helper for API calls – avoids pulling axios into landing bundle
const API_BASE = import.meta.env.VITE_API_BASE_URL || 'https://munger-api-898783762591.us-central1.run.app/api';

interface Slot {
  start: string; // ISO strings from backend
  end: string;
}

const PublicBooking: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  // --- User info and settings state ---
  const [userInfo, setUserInfo] = useState<{ name: string; description?: string } | null>(null);
  const [bookingSettings, setBookingSettings] = useState<{
    pageName?: string;
    welcomeMessage?: string;
    meetingDuration?: number;
    confirmationMessage?: string;
  } | null>(null);
  
  /* UI state */
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [slots, setSlots] = useState<Slot[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [step, setStep] = useState<'choose' | 'details' | 'confirm' | 'success'>('choose');
  const [submitting, setSubmitting] = useState(false);
  const [meetingLink, setMeetingLink] = useState<string | null>(null);

  // --- Helpers ----------------------------------------------------------
  const fetchSlots = async (date: Date) => {
    if (!slug) return;
    setLoading(true);
    setError(null);
    try {
      const from = startOfDay(date).toISOString();
      // Only fetch slots for the selected day to reduce query time
      const to = endOfDay(date).toISOString();
      const resp = await fetch(`${API_BASE}/booking/slug/${slug}/slots?from=${from}&to=${to}`);
      if (!resp.ok) {
        throw new Error(`Server responded ${resp.status}`);
      }
      const data = await resp.json();
      setSlots(data.slots || []);
    } catch (e: any) {
      setError(e.message || 'Failed fetching slots');
    } finally {
      setLoading(false);
    }
  };

  // Fetch slots and user info on mount
  useEffect(() => {
    fetchSlots(selectedDate);
    // Fetch user metadata and settings
    if (slug) {
      // Fetch basic user info
      fetch(`${API_BASE}/booking/slug/${slug}/info`)
        .then((res) => res.ok ? res.json() : null)
        .then((data) => setUserInfo(data))
        .catch(() => {});
      
      // Fetch booking settings
      fetch(`${API_BASE}/booking/slug/${slug}/settings`)
        .then((res) => res.ok ? res.json() : null)
        .then((data) => setBookingSettings(data))
        .catch(() => {
          // If settings endpoint doesn't exist yet, use defaults
          setBookingSettings({
            pageName: 'Book a Meeting',
            welcomeMessage: 'Schedule a meeting with me',
            meetingDuration: 30,
            confirmationMessage: 'Your booking has been confirmed!'
          });
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  // When user changes date manually
  const changeDate = (offset: number) => {
    const todayStart = startOfDay(new Date());
    const newDate = addDays(selectedDate, offset);
    // Prevent navigating to past days
    if (startOfDay(newDate) < todayStart) return;
    setSelectedDate(newDate);
    fetchSlots(newDate);
  };

  const submitBooking = async () => {
    if (!selectedSlot || !name || !email) return;
    setSubmitting(true);
    setError(null);
    try {
      const body = {
        name,
        email,
        slotStart: selectedSlot.start,
        slotEnd: selectedSlot.end,
      };
      const resp = await fetch(`${API_BASE}/booking/slug/${slug}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(data.error || `Server responded ${resp.status}`);
      }
      const result = await resp.json();
      // Store the meeting link from the response
      if (result.meetingLink) {
        setMeetingLink(result.meetingLink);
      }
      setStep('success');
    } catch (e: any) {
      setError(e.message || 'Failed creating booking');
    } finally {
      setSubmitting(false);
    }
  };

  // --- Render helpers ---------------------------------------------------
  const groupedSlots = slots.filter((s) => isSameDay(new Date(s.start), selectedDate));
  const todayStart = startOfDay(new Date());

  // Get meeting duration from settings or default to 30
  const meetingDuration = bookingSettings?.meetingDuration || 30;

  const pageVariants = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20 }
  };

  return (
    <div className="min-h-screen bg-gray-950 relative overflow-hidden">
      {/* Animated gradient background */}
      <div className="absolute inset-0">
        <div className="absolute top-0 -left-4 w-72 h-72 bg-purple-700 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
        <div className="absolute top-0 -right-4 w-72 h-72 bg-blue-700 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
        <div className="absolute -bottom-8 left-20 w-72 h-72 bg-pink-700 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>
        <div className="absolute inset-0 bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 py-8 md:py-16">
        <AnimatePresence mode="wait">
          {step === 'choose' && (
            <motion.div
              key="choose"
              variants={pageVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={{ duration: 0.3 }}
              className="grid grid-cols-1 lg:grid-cols-3 gap-8"
            >
              {/* Left sidebar with user info */}
              <div className="lg:col-span-1 space-y-6">
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 }}
                  className="relative group"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl blur-lg group-hover:blur-xl transition-all opacity-75"></div>
                  <Card className="relative border-0 bg-gray-900/90 backdrop-blur-xl shadow-2xl">
                    <CardHeader className="pb-4">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center">
                          <Sparkles className="w-7 h-7 text-white" />
                        </div>
                        <Badge className="bg-purple-500/20 text-purple-300 border-purple-500/30 hover:bg-purple-500/30">
                          <Zap className="w-3 h-3 mr-1" />
                          AI-Powered
                        </Badge>
                      </div>
                      <CardTitle className="text-2xl text-white">
                        {bookingSettings?.pageName || userInfo?.name || 'Book a Meeting'}
                      </CardTitle>
                      <CardDescription className="text-gray-400 mt-2">
                        {bookingSettings?.welcomeMessage || userInfo?.description || `Schedule a ${meetingDuration}-minute meeting`}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex items-center gap-3 text-gray-300">
                        <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                          <Clock className="w-5 h-5 text-blue-400" />
                        </div>
                        <div>
                          <p className="text-sm font-medium">{meetingDuration} minutes</p>
                          <p className="text-xs text-gray-500">Duration</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 text-gray-300">
                        <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                          <Video className="w-5 h-5 text-purple-400" />
                        </div>
                        <div>
                          <p className="text-sm font-medium">Video conference</p>
                          <p className="text-xs text-gray-500">Google Meet</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 text-gray-300">
                        <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                          <Shield className="w-5 h-5 text-green-400" />
                        </div>
                        <div>
                          <p className="text-sm font-medium">Secure & Private</p>
                          <p className="text-xs text-gray-500">End-to-end encrypted</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>

                {/* Performance insights */}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  <Card className="border-gray-800 bg-gray-900/50 backdrop-blur-sm">
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-lg text-white flex items-center gap-2">
                          <Star className="w-5 h-5 text-yellow-500" />
                          Performance Insights
                        </CardTitle>
                        <Badge variant="outline" className="border-blue-500/30 text-blue-300">
                          <Sparkles className="w-3 h-3 mr-1" />
                          AI Insights
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-400">Meetings this week</span>
                        <span className="text-sm font-semibold text-green-400">+45%</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-400">Average rating</span>
                        <div className="flex gap-1">
                          {[...Array(5)].map((_, i) => (
                            <Star key={i} className="w-4 h-4 fill-yellow-500 text-yellow-500" />
                          ))}
                        </div>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-400">Response time</span>
                        <span className="text-sm font-semibold text-blue-400">2 hours</span>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              </div>

              {/* Main content area */}
              <div className="lg:col-span-2">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15 }}
                  className="relative group"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-purple-600/20 rounded-2xl blur-xl"></div>
                  <Card className="relative border-gray-800 bg-gray-900/80 backdrop-blur-xl shadow-2xl">
                    <CardHeader className="border-b border-gray-800">
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle className="text-2xl text-white">Select a Date & Time</CardTitle>
                          <CardDescription className="text-gray-400 mt-1">
                            Choose an available time slot that works best for you
                          </CardDescription>
                        </div>
                        <Badge className="bg-blue-500/20 text-blue-300 border-blue-500/30">
                          <CalendarIcon className="w-3 h-3 mr-1" />
                          {format(selectedDate, 'MMM yyyy')}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="p-6">
                      {/* Date navigation */}
                      <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-4 mb-6 border border-gray-700">
                        <div className="flex items-center justify-between">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => changeDate(-1)}
                            disabled={startOfDay(selectedDate) <= todayStart}
                            className="hover:bg-gray-700 text-gray-400 hover:text-white transition-colors"
                          >
                            <ChevronLeft className="w-5 h-5" />
                          </Button>
                          <div className="text-center">
                            <h3 className="text-xl font-semibold text-white">{format(selectedDate, 'EEEE')}</h3>
                            <p className="text-sm text-gray-400 mt-1">{format(selectedDate, 'MMMM d, yyyy')}</p>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => changeDate(1)}
                            className="hover:bg-gray-700 text-gray-400 hover:text-white transition-colors"
                          >
                            <ChevronRight className="w-5 h-5" />
                          </Button>
                        </div>
                      </div>

                      {/* Time slots */}
                      {loading ? (
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                          {[...Array(6)].map((_, i) => (
                            <Skeleton key={i} className="h-14 bg-gray-800/50" />
                          ))}
                        </div>
                      ) : error ? (
                        <Alert className="border-red-500/50 bg-red-500/10">
                          <AlertDescription className="text-red-400">{error}</AlertDescription>
                        </Alert>
                      ) : groupedSlots.length === 0 ? (
                        <div className="text-center py-16">
                          <div className="w-20 h-20 rounded-2xl bg-gray-800/50 flex items-center justify-center mx-auto mb-4">
                            <CalendarIcon className="w-10 h-10 text-gray-600" />
                          </div>
                          <p className="text-gray-400 text-lg">No available time slots for this date</p>
                          <p className="text-sm text-gray-600 mt-2">Please try another day</p>
                        </div>
                      ) : (
                        <motion.div 
                          className="grid grid-cols-2 md:grid-cols-3 gap-3"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ staggerChildren: 0.05 }}
                        >
                          {groupedSlots.map((slot, index) => (
                            <motion.div
                              key={slot.start}
                              initial={{ opacity: 0, scale: 0.9 }}
                              animate={{ opacity: 1, scale: 1 }}
                              transition={{ delay: index * 0.05 }}
                            >
                              <Button
                                variant="outline"
                                size="lg"
                                className="w-full h-14 font-medium bg-gray-800/50 border-gray-700 text-gray-300 hover:bg-gradient-to-r hover:from-purple-600 hover:to-blue-600 hover:text-white hover:border-transparent transition-all duration-300 backdrop-blur-sm"
                                onClick={() => {
                                  setSelectedSlot(slot);
                                  setStep('details');
                                }}
                              >
                                {format(new Date(slot.start), 'h:mm a')}
                              </Button>
                            </motion.div>
                          ))}
                        </motion.div>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              </div>
            </motion.div>
          )}

          {step === 'details' && selectedSlot && (
            <motion.div
              key="details"
              variants={pageVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={{ duration: 0.3 }}
              className="max-w-2xl mx-auto"
            >
              <Button
                variant="ghost"
                className="mb-6 text-gray-400 hover:text-white hover:bg-gray-800"
                onClick={() => setStep('choose')}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to time selection
              </Button>

              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-r from-purple-600/20 to-blue-600/20 rounded-2xl blur-xl"></div>
                <Card className="relative border-gray-800 bg-gray-900/80 backdrop-blur-xl shadow-2xl">
                  <CardHeader className="border-b border-gray-800">
                    <CardTitle className="text-2xl text-white">Enter Your Details</CardTitle>
                    <CardDescription className="text-gray-400">
                      Please provide your information to confirm the booking
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="p-6">
                    <div className="mb-6 p-4 bg-gradient-to-r from-purple-600/20 to-blue-600/20 rounded-xl border border-purple-500/30">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-purple-300 font-medium mb-1">Selected time</p>
                          <p className="text-lg font-semibold text-white">
                            {format(new Date(selectedSlot.start), 'EEEE, MMMM d')}
                          </p>
                          <p className="text-gray-400 text-sm mt-1">
                            {format(new Date(selectedSlot.start), 'h:mm a')} - {format(new Date(selectedSlot.end), 'h:mm a')}
                          </p>
                        </div>
                        <Badge className="bg-purple-500/20 text-purple-300 border-purple-500/30">
                          <Clock className="w-3 h-3 mr-1" />
                          {meetingDuration} min
                        </Badge>
                      </div>
                    </div>

                    <form className="space-y-5" onSubmit={(e) => { e.preventDefault(); submitBooking(); }}>
                      <div className="space-y-2">
                        <Label htmlFor="name" className="text-gray-300">Name *</Label>
                        <Input
                          id="name"
                          type="text"
                          placeholder="John Doe"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          required
                          className="h-12 bg-gray-800/50 border-gray-700 text-white placeholder-gray-500 focus:border-purple-500 focus:ring-purple-500/20"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-gray-300">Email *</Label>
                        <Input
                          id="email"
                          type="email"
                          placeholder="john@example.com"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          required
                          className="h-12 bg-gray-800/50 border-gray-700 text-white placeholder-gray-500 focus:border-purple-500 focus:ring-purple-500/20"
                        />
                      </div>

                      {error && (
                        <Alert className="border-red-500/50 bg-red-500/10">
                          <AlertDescription className="text-red-400">{error}</AlertDescription>
                        </Alert>
                      )}

                      <div className="pt-4">
                        <Button
                          type="submit"
                          size="lg"
                          className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-medium transition-all duration-300"
                          disabled={submitting || !name || !email}
                        >
                          {submitting ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Scheduling...
                            </>
                          ) : (
                            <>
                              <Sparkles className="w-4 h-4 mr-2" />
                              Schedule Meeting
                            </>
                          )}
                        </Button>
                      </div>
                    </form>
                  </CardContent>
                </Card>
              </div>
            </motion.div>
          )}

          {step === 'success' && (
            <motion.div
              key="success"
              variants={pageVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={{ duration: 0.3 }}
              className="max-w-2xl mx-auto text-center"
            >
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-r from-green-600/20 to-blue-600/20 rounded-2xl blur-xl"></div>
                <Card className="relative border-gray-800 bg-gray-900/80 backdrop-blur-xl shadow-2xl">
                  <CardContent className="pt-12 pb-8">
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: "spring", duration: 0.5 }}
                      className="w-20 h-20 rounded-2xl bg-gradient-to-br from-green-500 to-blue-500 flex items-center justify-center mx-auto mb-6"
                    >
                      <CheckCircle2 className="w-10 h-10 text-white" />
                    </motion.div>

                    <h2 className="text-3xl font-bold text-white mb-3">Booking Confirmed!</h2>
                    <p className="text-lg text-gray-400 mb-8">
                      {bookingSettings?.confirmationMessage || `A confirmation email with calendar invite has been sent to ${email}`}
                    </p>

                    <Card className="bg-gray-800/50 border-gray-700 mb-8">
                      <CardContent className="pt-6">
                        <div className="space-y-4 text-left">
                          <div className="flex items-center justify-between">
                            <span className="text-gray-400">Date</span>
                            <span className="font-medium text-white">
                              {selectedSlot && format(new Date(selectedSlot.start), 'EEEE, MMMM d, yyyy')}
                            </span>
                          </div>
                          <Separator className="bg-gray-700" />
                          <div className="flex items-center justify-between">
                            <span className="text-gray-400">Time</span>
                            <span className="font-medium text-white">
                              {selectedSlot && format(new Date(selectedSlot.start), 'h:mm a')}
                            </span>
                          </div>
                          <Separator className="bg-gray-700" />
                          <div className="flex items-center justify-between">
                            <span className="text-gray-400">Duration</span>
                            <span className="font-medium text-white">{meetingDuration} minutes</span>
                          </div>
                          <Separator className="bg-gray-700" />
                          <div className="flex items-center justify-between">
                            <span className="text-gray-400">Meeting type</span>
                            <Badge className="bg-purple-500/20 text-purple-300 border-purple-500/30">
                              <Video className="w-3 h-3 mr-1" />
                              Video Call
                            </Badge>
                          </div>
                          {meetingLink && (
                            <>
                              <Separator className="bg-gray-700" />
                              <div className="flex items-center justify-between">
                                <span className="text-gray-400">Meeting Link</span>
                                <a 
                                  href={meetingLink} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-purple-400 hover:text-purple-300 transition-colors flex items-center gap-1"
                                >
                                  <Video className="w-3 h-3" />
                                  Join Meeting
                                </a>
                              </div>
                            </>
                          )}
                        </div>
                      </CardContent>
                    </Card>

                    {meetingLink && (
                      <Alert className="border-purple-500/50 bg-purple-500/10 mb-6">
                        <AlertDescription className="text-purple-300">
                          <strong>Save this link:</strong> {meetingLink}
                          <br />
                          <span className="text-sm text-purple-400 mt-1 block">
                            A calendar invite with this link has been sent to your email.
                          </span>
                        </AlertDescription>
                      </Alert>
                    )}

                    <Button
                      size="lg"
                      onClick={() => navigate('/')}
                      className="px-8 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-medium transition-all duration-300"
                    >
                      <Sparkles className="w-4 h-4 mr-2" />
                      Back to home
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <style jsx>{`
        @keyframes blob {
          0% {
            transform: translate(0px, 0px) scale(1);
          }
          33% {
            transform: translate(30px, -50px) scale(1.1);
          }
          66% {
            transform: translate(-20px, 20px) scale(0.9);
          }
          100% {
            transform: translate(0px, 0px) scale(1);
          }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
    </div>
  );
};

export default PublicBooking; 